import React, { useEffect, useState } from "react";
import api from "../../utils/axios.config";
import { toast } from "react-toastify";

const VideoGalleryManagement = () => {
  const [videos, setVideos] = useState([]);
  const [newVideo, setNewVideo] = useState({
    title: "",
    description: "",
    videoUrl: "",
  });
  const [editingVideo, setEditingVideo] = useState(null);

  const fetchVideos = async () => {
    try {
      const { data } = await api.get("/videos");
      setVideos(data);
    } catch (error) {
      toast.error("Failed to fetch videos.");
      console.error("Error fetching videos:", error);
    }
  };

  useEffect(() => {
    fetchVideos();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (editingVideo) {
      setEditingVideo({ ...editingVideo, [name]: value });
    } else {
      setNewVideo({ ...newVideo, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingVideo) {
        await api.put(`/videos/${editingVideo._id}`, editingVideo);
        toast.success("Video updated successfully!");
      } else {
        await api.post("/videos", newVideo);
        toast.success("Video added successfully!");
      }
      setNewVideo({
        title: "",
        description: "",
        videoUrl: "",
      });
      setEditingVideo(null);
      fetchVideos();
    } catch (error) {
      toast.error("Failed to save video.");
      console.error("Error saving video:", error);
    }
  };

  const handleEdit = (video) => {
    setEditingVideo(video);
    setNewVideo({
      title: "",
      description: "",
      videoUrl: "",
    }); // Clear new video form
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this video?")) {
      try {
        await api.delete(`/videos/${id}`);
        toast.success("Video deleted successfully!");
        fetchVideos();
      } catch (error) {
        toast.error("Failed to delete video.");
        console.error("Error deleting video:", error);
      }
    }
  };

  return (
    <div className="p-4">
      <h2 className="text-2xl font-bold mb-4">Video Gallery Management</h2>

      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <h3 className="text-xl font-semibold mb-4">
          {editingVideo ? "Edit Video" : "Add New Video"}
        </h3>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label
              className="block text-gray-700 text-sm font-bold mb-2"
              htmlFor="title"
            >
              Title
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={editingVideo ? editingVideo.title : newVideo.title}
              onChange={handleChange}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              required
            />
          </div>
          <div className="mb-4">
            <label
              className="block text-gray-700 text-sm font-bold mb-2"
              htmlFor="description"
            >
              Description
            </label>
            <textarea
              id="description"
              name="description"
              value={
                editingVideo ? editingVideo.description : newVideo.description
              }
              onChange={handleChange}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              rows="3"
            ></textarea>
          </div>
          <div className="mb-4">
            <label
              className="block text-gray-700 text-sm font-bold mb-2"
              htmlFor="videoUrl"
            >
              Video URL (YouTube/Vimeo/Facebook link)
            </label>
            <input
              type="url"
              id="videoUrl"
              name="videoUrl"
              value={editingVideo ? editingVideo.videoUrl : newVideo.videoUrl}
              onChange={handleChange}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              required
            />
          </div>
          <div className="flex items-center justify-between">
            <button
              type="submit"
              className="bg-primary  text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
            >
              {editingVideo ? "Update Video" : "Add Video"}
            </button>
            {editingVideo && (
              <button
                type="button"
                onClick={() => setEditingVideo(null)}
                className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline ml-2"
              >
                Cancel Edit
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-md">
        <h3 className="text-xl font-semibold mb-4">Existing Videos</h3>
        {videos.length === 0 ? (
          <p>No videos found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Title
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Video URL
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {videos.map((video) => (
                  <tr key={video._id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {video.title}
                      </div>
                      <div className="text-sm text-gray-500">
                        {video.description}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600 underline">
                      <a
                        href={video.videoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {video.videoUrl}
                      </a>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEdit(video)}
                        className="text-indigo-600 hover:text-indigo-900 mr-3"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(video._id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoGalleryManagement;
